// src/features/apps/SubmissionGuideUtils.ts
//
// The store guide's freshness (3.34.2; appfactory ADR-0035). A Roku
// submission's guide and deep-link test values are frozen when the build
// passes preflight; a customer who changes content before submitting can be
// holding a guide that names content the app no longer has (found on the
// demo's 09-22 guide, whose titles read as stale after a catalog rebuild —
// the items were in fact still in the feed, and Roku tests by id). The farm
// refreshes the guide on request — the holder records the transition
// awaiting_customer -> preparing, the farm re-derives and re-tests the
// values on a device and the same submission returns to awaiting_customer.
// These helpers decide what the card SAYS: which frozen values the app's
// feed no longer carries, and when the refresh applies.

import type { FactoryDeeplinkValue, FactorySubmission } from '@/lib/types/AppFactory';
import type { RokuFeedDoc } from '@/lib/types/RokuFeed';

/** The one store whose guide carries device-tested deep-link values. */
export const GUIDE_REFRESH_STORE = 'roku';
/** The submission state the refresh is recorded from and returns to. */
export const GUIDE_REFRESH_FROM_STATE = 'awaiting_customer';
/** The state the refresh moves the submission to while the farm re-tests. */
export const GUIDE_REFRESH_TO_STATE = 'preparing';
/** The note the refresh records on the submission's event trail. */
export const GUIDE_REFRESH_NOTE = 'store guide refresh';

/** How deep the feed walk looks for item ids (the feed nests items under a few array keys). */
const FEED_WALK_MAX_DEPTH = 6;

export class SubmissionGuideUtils {
  /**
   * Every item id the feed carries, wherever the document nests them
   * (liveFeeds, tvSpecials, categories, playlists, series/episodes…): the
   * legacy feed's `id` strings. Empty when the body is not a feed.
   */
  static feedItemIds(feed: unknown): Set<string> {
    const ids = new Set<string>();
    const walk = (node: unknown, depth: number) => {
      if (depth > FEED_WALK_MAX_DEPTH || node === null || typeof node !== 'object') return;
      if (Array.isArray(node)) {
        for (const child of node) walk(child, depth + 1);
        return;
      }
      const o = node as Record<string, unknown>;
      if (typeof o.id === 'string' && o.id.length > 0) ids.add(o.id);
      for (const v of Object.values(o)) if (v !== null && typeof v === 'object') walk(v, depth + 1);
    };
    walk(feed, 0);
    return ids;
  }

  /**
   * The frozen values the app's Roku feed no longer carries. The feed is the
   * deep-link derive's own source and lists every item, so a value whose
   * contentId (the feed item id, verbatim) appears nowhere in the feed is
   * gone. Roku tests deep links by content id, so a changed title alone is
   * not stale. An absent feed, or a body with no item ids at all, is
   * UNKNOWN, never stale — the warning must not fire on a feed that could
   * not be read.
   */
  static staleValues(
    values: FactoryDeeplinkValue[],
    feed: RokuFeedDoc | undefined,
  ): FactoryDeeplinkValue[] {
    const ids = SubmissionGuideUtils.feedItemIds(feed);
    if (ids.size === 0) return [];
    return values.filter((v) => !ids.has(v.contentId));
  }

  /** The refresh applies to a Roku submission waiting on the holder that carries test values. */
  static refreshable(submission: FactorySubmission, store: string): boolean {
    return (
      store === GUIDE_REFRESH_STORE &&
      submission.state === GUIDE_REFRESH_FROM_STATE &&
      Array.isArray(submission.deeplinkValues)
    );
  }

  /** Any submission of the app the farm is (re)preparing — the submissions poll trigger. */
  static hasPreparingSubmission(submissions: FactorySubmission[]): boolean {
    return submissions.some((s) => s.state === GUIDE_REFRESH_TO_STATE);
  }

  /** The warning under the deep-link table; null when nothing is stale. */
  static staleWarning(stale: FactoryDeeplinkValue[]): string | null {
    if (stale.length === 0) return null;
    const titles = stale.map((v) => v.title || v.contentId).join(', ');
    return `This guide names content that is no longer in your app: ${titles}. Refresh it before you submit.`;
  }
}
